import axios from 'axios';
import i18n from '../i18n';
import { IS_APP_BUILD } from './buildTarget';
import { getTokens, setTokens, clearTokens } from './tokenStore';
import { withNativeSignal } from './nativeAuth';
import { NET_VERDICT, subscribeNetworkVerdict, markNetworkEvidenceUp, markNetworkEvidenceDown } from './offline/networkVerdict';
import { logNetError } from './diag/netError';
import { log as diagLog } from './diag/readyChainLog';
import { nativeHttpAdapter } from './nativeHttpAdapter';

// PRODUCTION: hardcoded to quizessa.com: deploys cannot override (this protects against
// REACT_APP_BACKEND_URL being overwritten by Emergent preview URLs, which broke auth cookies
// 4 times historically). DO NOT change the production fallback.
//
// LOCAL DEV: when NODE_ENV !== 'production' (e.g. yarn start), reads REACT_APP_BACKEND_URL
// from frontend/.env, defaulting to http://localhost:8000. Production builds (yarn build)
// always use the hardcoded value regardless of env vars present at build time.
export const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://quizessa.com'
  : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000');

// F6.7b post-smoke — TECHO DE ESPERA. Hasta hoy axios NO fijaba `timeout`, así
// que cada petición esperaba lo que el sistema decidiera. En modo avión eso son
// los 10-20 s de limbo que Carlos midió on-device: la pantalla no puede pintar
// su estado honesto hasta que la petición condenada se rinda, y quien decide
// cuándo se rinde es iOS, no nosotros.
//
// 10 s es deliberadamente generoso para una lectura normal: el p99 real de este
// backend está muy por debajo, y el objetivo del techo no es cortar peticiones
// lentas sino poner un suelo bajo el peor caso. Con el vigía dictaminando
// offline las peticiones ni siquiera se lanzan (atajo offline) o se cancelan
// (abortAllInFlight, abajo); este techo es el cinturón para el limbo en que el
// vigía todavía no ha hablado.
export const DEFAULT_TIMEOUT_MS = 10_000;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: DEFAULT_TIMEOUT_MS,
  // Web: httpOnly cookies carry the session, so credentials must ride along.
  // App (bearer mode): withCredentials:false — cookies must NEVER travel. The
  // backend's get_current_user reads the cookie BEFORE the Authorization header,
  // so a stale/phantom WebView cookie would shadow a fresh bearer token and
  // produce spurious 401s. In native the session lives only in the Authorization
  // header (attached by the app-only request interceptor below).
  withCredentials: !IS_APP_BUILD,
  // F6.7b pasada final 3 — TRANSPORTE NATIVO, solo app-build. Las llamadas
  // JSON viajan por CapacitorHttp (URLSession) en vez del XHR del WebView:
  // tras un cambio de interfaz el pool del WebView puede quedarse con una
  // conexión zombi que atora TODO ~150 s (medido: log s78i8t); el pool del
  // sistema no comparte ese cadáver. FormData y binarios delegan al camino
  // actual DENTRO del adapter (ver nativeHttpAdapter.js). En web no se pasa
  // `adapter`: axios queda exactamente como siempre.
  ...(IS_APP_BUILD ? { adapter: nativeHttpAdapter } : {}),
});

// ── EXCEPCIÓN EXPLÍCITA AL TECHO: subir archivos ────────────────────────────
//
// Una subida NO es una lectura. Un PDF de 50 MB (el tope de la app) por una red
// celular mediocre tarda minutos legítimamente, y cortarla a los 10 s
// destruiría trabajo del usuario y consumiría su crédito para nada.
//
// La detección es estructural, no una lista de rutas: cualquier petición cuyo
// cuerpo sea un FormData es una subida de archivo. Así queda cubierta la subida
// de documentos (Dashboard) y la de imágenes de flashcard (Flashcards) — y
// cualquiera futura, sin que nadie tenga que acordarse de esta lista.
//
// REMATE-266 — Y AHORA TIENEN TECHO, aunque sea altísimo. Hasta hoy iban con
// `timeout: 0`, o sea SIN techo, y además por el adapter por defecto de axios
// (el XHR del WKWebView, porque `nativeHttpAdapter` delega cualquier FormData:
// una subida de 50 MB por URLSession sin streaming sería una regresión). Ese es
// el MISMO pool que el adapter nativo existe para no usar: tras un cambio de
// interfaz puede quedarse con una conexión zombi que atora todo ~150 s. Una
// subida en vuelo cuando la red se vuelve zombie se quedaba colgada
// indefinidamente ahí, sin techo que la rindiera y sin nadie que la mirara.
//
// 180 000 ms (3 minutos) es deliberadamente enorme para una lectura y
// deliberadamente FINITO para una subida: cubre con holgura un PDF de 50 MB por
// una red celular mediocre —el caso que el `timeout: 0` protegía— y le pone
// suelo al peor caso, que es el que hoy no tenía ninguno. El transporte NO se
// mueve en esta tanda: las subidas siguen por el camino de siempre.
//
// `__sbNoTimeout: true` en la config es la puerta de escape manual para una
// petición larga que no lleve FormData, y ESA sí sigue sin techo. Hoy no la usa
// nadie; el día que alguien la use, la escapatoria funciona igual que siempre.
export const UPLOAD_TIMEOUT_MS = 180_000;

// OCR-ALUMNO (PR #347, §C2): la subida del alumno (PDF y paquete de fotos) puede
// clasificar y sondear con visión antes de contestar; esas DOS rutas —y sólo esas—
// llevan un techo de 10 minutos. El resto de las subidas sigue en 3.
export const UPLOAD_ALUMNO_TIMEOUT_MS = 600_000;
const RUTAS_SUBIDA_ALUMNO = ['/documents/upload', '/documents/upload-fotos'];

function isFormDataUpload(config) {
  return typeof FormData !== 'undefined' && config.data instanceof FormData;
}

function esSubidaDelAlumno(config) {
  const url = String(config.url || '');
  return RUTAS_SUBIDA_ALUMNO.some((r) => url === r || url.endsWith(r));
}

// REMATES-350 (PR #351, R-5 a1 / #348) — EL TECHO QUE MANDA EL LLAMADOR, MANDA.
//
// La subida del banco del profesor y su grabación viajan con `{ timeout: 20 min }`
// (`GroupBankSection.js`) porque 500 MB por una conexión de casa se pasan de los
// tres minutos sin que nada vaya mal. Hasta hoy ese número no llegaba a axios:
// el interceptor de abajo pisaba CUALQUIER FormData con `UPLOAD_TIMEOUT_MS` sin
// mirar la config, y las dos subidas morían a los 180 s exactos con ECONNABORTED
// —la pantalla lo pintaba como «No se pudo subir este documento»—. En la app es
// el mismo camino (el FormData va por el XHR del WebView) y el mismo techo.
//
// La config llega aquí ya fundida con los defaults del cliente, así que «el
// llamador puso techo» es «`timeout` es un número distinto de `DEFAULT_TIMEOUT_MS`».
// Un FormData sin techo propio sigue exactamente igual que antes (180 s, o 10 min
// en las dos rutas del alumno); la puerta de escape (`__sbNoTimeout`) sigue mandando
// sobre todo lo demás.
function esTechoDelLlamador(config) {
  return typeof config.timeout === 'number' && config.timeout !== DEFAULT_TIMEOUT_MS;
}

// El techo que le toca a esta petición, o `undefined` si hereda el del cliente.
function uploadTimeoutFor(config) {
  if (config.__sbNoTimeout === true) return 0;                 // escape manual: sin techo
  if (isFormDataUpload(config) && esTechoDelLlamador(config)) return undefined; // R-5 a1: el suyo
  if (isFormDataUpload(config) && esSubidaDelAlumno(config)) return UPLOAD_ALUMNO_TIMEOUT_MS;
  if (isFormDataUpload(config)) return UPLOAD_TIMEOUT_MS;       // subida: techo alto pero finito
  return undefined;                                            // lectura normal: DEFAULT_TIMEOUT_MS
}

// REMATES-350 (PR #351, R-5 a2 / #348) — UN 401 TRAS SUBIR NO RE-ENVÍA EL ARCHIVO.
//
// El access token vive 15 minutos (`ACCESS_TOKEN_TTL_SECONDS`, routes/auth.py).
// Una subida más larga que eso —390 MB por una conexión de casa— termina en un
// 401 que llega DESPUÉS de haber mandado todos los bytes, y el interceptor de
// refresh reintentaba con `api(originalRequest)`: la MISMA config, FormData
// incluido, o sea el archivo entero viajando otra vez en silencio, con el botón
// «cargando» y sin que nadie lo hubiera pedido. El refresh SÍ se hace (para que
// el reintento explícito salga con sesión fresca); lo que no se repite es la
// petición: el 401 llega a su llamador, que lo avisa una vez y ofrece reintentar.
// Toda petición que no sea FormData sigue reintentándose como siempre.
function reintentoTrasRefresh(originalRequest, err) {
  if (isFormDataUpload(originalRequest)) return Promise.reject(err);
  return api(originalRequest);
}

// ── CANCELACIÓN AL PERDER LA SEÑAL ──────────────────────────────────────────
//
// Registro de peticiones en vuelo. Cuando el vigía DICTAMINA offline no tiene
// ningún sentido que una petición ya lanzada siga esperando su techo de 10 s:
// se aborta y la pantalla llega a su estado honesto de inmediato.
//
// Un abort de axios produce un error SIN `response`, así que
// `isNetworkShapedError` lo clasifica como forma-de-red y `isAuthRejection` lo
// descarta — o sea, una cancelación recorre EXACTAMENTE el mismo camino que una
// red caída: las pantallas caen a bodega, y ni el interceptor de 401 ni el boot
// nativo destruyen la sesión. Eso es lo que hace que esto sea seguro.
const inFlightControllers = new Set();

/**
 * Aborta todas las peticiones en vuelo que este módulo controla.
 * @returns {number} cuántas se abortaron (para el visor y los tests).
 *
 * NO toca peticiones que trajeron su propia `signal` (el POST de resize de
 * imagen de Flashcards): esas tienen dueño y su dueño decide cuándo cancelarlas.
 */
export function abortAllInFlight() {
  const victims = Array.from(inFlightControllers);
  inFlightControllers.clear();
  for (const c of victims) {
    try { c.abort(); } catch (e) { /* un abort que falla no rompe a los demás */ }
  }
  return victims.length;
}

function releaseController(config) {
  const c = config && config.__sbController;
  if (c) inFlightControllers.delete(c);
}

let _csrfToken = '';
let _lastSessionAt = 0;
let _onSessionExpired = null;

export const setCsrfToken = (token) => {
  _csrfToken = token;
  if (token) _lastSessionAt = Date.now();
};
export const getCsrfToken = () => _csrfToken;
export const registerSessionExpiredHandler = (handler) => { _onSessionExpired = handler; };

// REMATES-350 (PR #351, R-4 / #348) — EL REFRESCO PREVIO A UNA SUBIDA GRANDE.
//
// El access token vive 15 minutos y una subida de cientos de MB puede durar más:
// si caduca a media subida, el servidor contesta 401 DESPUÉS de recibir todos los
// bytes (ver `reintentoTrasRefresh`). Antes de arrancar una subida de más de 50 MB
// el módulo de subidas del banco (`lib/subidasDelBanco.js`) refresca la sesión por
// AQUÍ, que es el mismo camino del interceptor de 401: cookie en web, refresh token
// en el cuerpo por el transporte nativo en app, y el par rotado guardado antes de
// devolver. Devuelve `true` si la sesión quedó fresca y `false` si el refresh
// falló, sin tocar nada en ese caso: la subida sale igual (a lo mejor el token
// todavía alcanza) y, si llega el 401, se avisa una vez. Nunca expulsa a login.
export async function refrescarSesion() {
  try {
    let res;
    if (IS_APP_BUILD) {
      const stored = await getTokens();
      res = await axios.post(`${API_BASE}/api/auth/refresh`,
        withNativeSignal({ refresh_token: stored?.refresh_token }),
        { withCredentials: false, adapter: nativeHttpAdapter });
      await setTokens(res.data);
    } else {
      res = await axios.post(`${API_BASE}/api/auth/refresh`, {}, { withCredentials: true });
    }
    _csrfToken = res.data.csrf_token;
    _lastSessionAt = Date.now();
    return true;
  } catch (e) {
    return false;
  }
}

// F6.7b post-smoke — techo por petición + alta en el registro de en-vuelo.
// Interceptor propio y separado a propósito: los dos de abajo son de F3/F4 y no
// se tocan. Solo se adopta la petición si NO trae `signal` propia — quien la
// trae ya tiene dueño (ver abortAllInFlight).
api.interceptors.request.use((config) => {
  const techo = uploadTimeoutFor(config);
  if (techo !== undefined) config.timeout = techo;
  // F6.7b post-smoke (2ª pasada) — UNA SIGNAL MUERTA NO PUEDE VIAJAR EN UN
  // REINTENTO. El camino de reintento tras refrescar (`api(originalRequest)`,
  // abajo) reusa la MISMA config del primer intento, `signal` incluida. Si esa
  // signal es nuestra y ya fue abortada —el vigía soltó las peticiones al
  // perder la señal—, el reintento nacería cancelado: se rechaza antes de
  // salir a la red y la pantalla ve "fallo de red" con la red buena. Se
  // reemplaza por un controlador nuevo. Una signal AJENA (dueño propio, p. ej.
  // el resize de imagen) no se toca nunca: su dueño decidió cancelarla.
  const own = config.__sbController;
  if (own && config.signal === own.signal && own.signal.aborted) {
    inFlightControllers.delete(own);
    config.signal = undefined;
    config.__sbController = undefined;
  }
  if (!config.signal) {
    const controller = new AbortController();
    config.signal = controller.signal;
    config.__sbController = controller;
    inFlightControllers.add(controller);
  }
  return config;
});

api.interceptors.request.use((config) => {
  // X-CSRF-Token is a WEB-ONLY concern: a bearer session carries no ambient
  // cookie, so it is immune to CSRF by construction and the app build never
  // sends the header (inert on the backend, and less surface). In the web build
  // IS_APP_BUILD is false, so this stays the pre-F3 CSRF condition, unchanged.
  if (!IS_APP_BUILD && _csrfToken && ['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
    config.headers['X-CSRF-Token'] = _csrfToken;
  }
  // Attach UTM attribution body to guest creation and signup endpoints.
  // Backend persists as users.attribution_initial (first-touch — never overwritten).
  try {
    if (
      config.method === 'post' &&
      /\/auth\/(guest|signup)$/.test(config.url || '')
    ) {
      const attr = localStorage.getItem('utm_attribution');
      // post-t7-1 (F1, #580): la atribucion que ya viene en el cuerpo (la del clic de "Start Free") no se pisa
      if (attr && !(config.data && config.data.attribution)) {
        config.data = { ...(config.data || {}), attribution: JSON.parse(attr) };
      }
    }
  } catch (e) {
    // Silent failure — attribution is best-effort
  }
  // TANDA 24b (#233) — EL IDIOMA DE LOS CORREOS QUE NO TIENEN USUARIO. El correo de
  // verificación (registro y reenvío del código) se dispara cuando el destinatario
  // todavía no existe en `users`: no hay a quién preguntarle el idioma, y guardarlo
  // estaba prohibido. Viaja aquí, en un encabezado de la lista segura de CORS (no
  // dispara preflight) y con `i18n.language`, que es el idioma REAL de la app y no
  // el de la configuración del navegador. El backend lo lee sin guardarlo
  // (`_idioma_de_la_peticion`, routes/auth.py) y cae a "en" si no llega.
  config.headers['Accept-Language'] = i18n.language || 'en';
  return config;
});

// App build only: attach the bearer access token from the store. Registered as a
// SEPARATE (async) interceptor so the web request interceptor above stays
// synchronous and untouched — in the web build IS_APP_BUILD is false at runtime, so
// this registration is simply skipped (there is no async interceptor on web).
// Because it re-reads the store on every request (including retries after a
// refresh), a rotated access token is picked up automatically.
if (IS_APP_BUILD) {
  api.interceptors.request.use(async (config) => {
    const tokens = await getTokens();
    if (tokens?.access_token) {
      config.headers['Authorization'] = `${tokens.token_type || 'Bearer'} ${tokens.access_token}`;
    }
    return config;
  });
}

// ÚNICA fuente de verdad para "¿el backend RECHAZÓ esta sesión?" — usada por
// este interceptor Y por el cold-start nativo de AuthContext. Solo un 401/403
// del propio refresh prueba que el token es inválido. TODO lo demás — sin
// `response` (red caída / timeout / DNS), 5xx, 429 — es TRANSITORIO: no dice
// nada sobre el token, así que jamás debe destruir la sesión (F6.α: un arranque
// en modo avión borraba los tokens del Keychain igual que un 401 real).
export const isAuthRejection = (err) =>
  err?.response?.status === 401 || err?.response?.status === 403;

// ÚNICA fuente de verdad para "¿este fallo tiene forma de red caída?" — el
// hermano de isAuthRejection. En F6.4b era además LA ÚNICA manera de "detectar
// offline" que tenía la app, y de ahí venía el paréntesis original de esta nota:
// «nada de navigator.onLine ni @capacitor/network — el plugin lo decide F6.7».
//
// ACTUALIZADO F6.8 T5 — eso era la doctrina DE F6.4b y ya no describe el repo:
// @capacitor/network SÍ está instalado desde la T5, y el estado de la red lo
// dictamina el veredicto (lib/offline/networkVerdict.js), que el sensor nativo
// escribe con su semilla y sus avisos de cambio. Este archivo incluso lo
// alimenta: el interceptor de respuesta llama markNetworkEvidenceUp() con cada
// respuesta exitosa (api.js:393, T5-4).
//
// Lo que NO cambió es esta función, ni un byte: clasifica la FORMA de un fallo
// que ya ocurrió, no el estado de la red, y por eso sigue siendo el criterio de
// forma-de-red de toda la app. La prohibición que sigue viva es navigator.onLine
// (regla 1 de networkVerdict.js): dictamen, jamás sospecha.
//
// true cuando NO hay `response` (red caída / timeout / DNS) o cuando el server
// contestó con algo TRANSITORIO que no dice nada del dato ni de la sesión
// (5xx, 429). Es deliberadamente el complemento de isAuthRejection: un fallo
// puede ser rechazo de sesión (auth), forma-de-red (esto), o un 4xx real del
// endpoint (ninguno de los dos — el caller lo trata como error normal).
export const isNetworkShapedError = (err) => {
  const status = err?.response?.status;
  if (status === undefined) return true;
  return status >= 500 || status === 429;
};

// F6.8 EH-2 — LA FRONTERA, y por qué NO alcanzaba con la de arriba.
//
// `isNetworkShapedError` contesta una pregunta de MANEJO: «¿puedo caer a la
// bodega y reintentar más tarde?». Para eso un 5xx y una red caída son lo mismo,
// y meterlos en la misma bolsa es correcto. Pero cuando lo que se decide es QUÉ
// LEE EL USUARIO, esa bolsa MIENTE: un 5xx llegó y volvió, o sea que había señal
// viva, y decirle "sin conexión" a alguien con señal es exactamente el bug que
// el lookup documentó (C-2, `Analytics.corteBodega.test.js:280`).
//
// SIN `response` LA PETICIÓN NUNCA LLEGÓ AL SERVIDOR: red caída, DNS, timeout,
// abort, o el error sintético que las pantallas lanzan con dictamen de sin señal
// (`offlineShapedError`). Eso, y sólo eso, se le puede llamar "sin conexión".
//
// NO REEMPLAZA a su hermana ni le toca un byte: conviven porque responden dos
// preguntas distintas. La otra mitad de la frontera no es un error sino el
// VEREDICTO vivo del vigía (`useNetworkStatus`), que las pantallas ya consumen.
export const isOfflineShapedError = (err) => err?.response?.status === undefined;

// REMATE-266 — ¿ESTE FALLO LO CAUSAMOS NOSOTROS? LA GUARDA CONTRA EL BUCLE.
//
// Es la pieza sin la cual la evidencia hacia abajo se muerde la cola, y el
// mecanismo es exacto: al dictaminarse DOWN, `abortAllInFlight()` (abajo) suelta
// TODAS las peticiones en vuelo, y cada abort produce un error SIN `response`
// —o sea, con la forma exacta de una red caída, que es lo que hace que la
// cancelación sea segura para el resto de la app—. Sin esta exclusión el primer
// DOWN fabricaría de golpe su propia evidencia para el siguiente, y el
// veredicto quedaría auto-confirmándose para siempre a partir de un solo
// episodio.
//
// Vale para las DOS clases de cancelación, y las dos por la misma razón (una
// cancelación no dice NADA sobre la red): la nuestra —`abortAllInFlight`— y la
// ajena, la de una petición que trajo su propia `signal` y cuyo dueño decidió
// soltarla (el POST de resize de imagen de Flashcards).
//
// Tres detectores en vez de uno porque los dos transportes construyen el error
// por caminos distintos: axios lanza `CanceledError` (que `axios.isCancel`
// reconoce por su marca `__CANCEL__`) y `nativeHttpAdapter` construye el suyo
// con `new CanceledError(null, config)` — misma clase, mismo `ERR_CANCELED`.
export const isOwnCancel = (err) => {
  try { if (axios.isCancel(err)) return true; } catch (e) { /* un detector que truena no decide */ }
  return err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) { prom.reject(error); } else { prom.resolve(); }
  });
  failedQueue = [];
};

function formatRateLimitMessage(detail) {
  if (typeof detail !== 'string') return detail;
  const match = detail.match(/(\d+)\s*seconds/);
  if (!match) return detail;
  const secs = parseInt(match[1], 10);
  let formatted;
  if (secs < 60) {
    formatted = i18n.t('common:time.second', { count: secs });
  } else if (secs < 3600) {
    formatted = i18n.t('common:time.minute', { count: Math.round(secs / 60) });
  } else if (secs < 86400) {
    formatted = i18n.t('common:time.hour', { count: Math.floor(secs / 3600) });
  } else {
    formatted = i18n.t('common:time.day', { count: Math.floor(secs / 86400) });
  }
  return detail.replace(/\d+\s*seconds/, formatted);
}

// ── TANDA 8 (#168) — EL ÚNICO PUNTO DE TRADUCCIÓN DEL TEXTO DE SERVIDOR ───────────
//
// Antes había CINCO frases traducidas y todo lo demás pasaba en crudo (el `return
// detail` de más abajo lo decía con todas sus letras). Traducir por la frase es frágil
// de la peor manera: cambiar una coma en el backend deja al usuario sin traducción y
// nadie se entera. Ahora el backend nombra cada falla con un `error_code` estable y la
// traducción se busca por ese código, no por el texto.
//
// Las tres reglas, en este orden:
//   (a) hay `error_code` conocido        -> se devuelve el traducido
//   (b) el detail es el sobre {error|code, message} -> NO se aplana: se traduce el
//       `message` EN SU LUGAR y el resto del objeto llega intacto a quien lo lea
//   (c) cualquier otra cosa              -> pasa en crudo, exactamente como hoy
//
// Un código que este bundle no conozca cae a (c): el usuario ve el inglés del backend
// —feo pero legible— en vez de una clave sin traducir. Cero migración.
const BACKEND_ERROR_MAP = {
  // Las cinco de siempre. Siguen aquí porque NO nacen en `routes/`: las emiten FastAPI
  // y las dependencias de auth, que no pasan por la pieza central del backend.
  "Not authenticated": "common:errors.not_authenticated",
  "Token expired": "common:errors.token_expired",
  "Invalid token": "common:errors.invalid_token",
  "Rate limit exceeded. Please wait before trying again.": "common:errors.rate_limit_generic",
  "Login failed": "common:errors.login_failed",
  // El único Tier B que viaja SIN código en su sobre (manda {message, show_reset_link}).
  "Too many login attempts. Please try again in 15 minutes, or reset your password.":
    "common:errors.auth_too_many_login_attempts",
};

// Los detalles que llevan variables dentro del texto. El backend ya las interpoló en el
// inglés, así que para rearmar la frase en español hay que sacarlas de ahí: cada patrón
// está anclado al `detail` ratificado, que es byte-idéntico y no se mueve.
// Si un patrón no casa, no se traduce — nunca se pinta un `{{n}}` a medio rellenar.
const EXTRACTORES = {
  doc_limit_reached: [/document limit \((\d+) active/, ['n']],
  doc_unsupported_file_type: [/^Unsupported file type: (.+?)\. Allowed: (.+)$/, ['ext', 'allowed']],
  doc_file_too_large: [/^File exceeds (\d+) MB/, ['n']],
  study_question_too_long: [/^Question must be (\d+) characters/, ['n']],
  study_answer_too_long: [/^Answer must be (\d+) characters/, ['n']],
  support_message_too_long: [/^Message too long\. Maximum (\d+) characters/, ['n']],
  pay_plan_already_active: [/^You already have the (.+) plan or higher$/, ['plan']],
  pay_upgrade_failed: [/^Upgrade failed: ([\s\S]+)$/, ['detail']],
  doc_file_unreadable_format: [/^Could not read this (\S+) file/, ['ext']],
  // TANDA 24 (A2.7): el tope de grupos. Es el mismo código para las dos mitades
  // —20 para el alumno, 30 para el profesor— porque es el mismo hecho con otro
  // número, y el número sale del inglés que el backend ya interpoló. El patrón
  // está anclado a los dos extremos: si el backend reescribiera la frase, esto
  // deja de casar y la regla de la casa manda NO traducir antes que pintar un
  // `{{n}}` a medio rellenar.
  group_limit_reached: [/^You have reached the limit of (\d+) groups\.$/, ['n']],
  // TANDA 26 (firma #234): el tope de documentos DEL GRUPO, con el mismo molde.
  // El numero no es una constante del cliente —lo fija `MAX_DOCS_PER_GROUP` en el
  // servidor— asi que viaja interpolado en el ingles y de ahi se saca para volver
  // a meterlo en el `{{n}}` de la frase traducida. Anclado a los dos extremos: si
  // el backend reescribiera la frase, esto deja de casar y no se traduce, que es
  // la regla de la casa (mejor el ingles entero que un `{{n}}` a medio rellenar).
  bank_group_doc_limit: [/^This group reached its limit of (\d+) documents\.$/, ['n']],
  // El prefijo máquina de [CHAR_LIMIT] no se toca en el backend; aquí sólo se le sacan
  // los números para poder interpolar, y lo que se devuelve es la prosa traducida.
  doc_char_limit: [/^\[CHAR_LIMIT\]\|(\d+)\|/, ['n']],
};

// Los códigos que traen su propio plural en el locale, y de qué campo sale el `count`.
const CAMPO_DE_PLURAL = {
  auth_wrong_code_attempts_left: null,       // el count sale del propio texto
  auth_wrong_code_attempts_left_conv: null,
  insufficient_credits: 'credits_required',  // Tier B: el sobre ya trae el número
};

const EXTRACTORES_PLURAL = {
  auth_wrong_code_attempts_left: /\. (\d+) attempts? remaining\./,
  auth_wrong_code_attempts_left_conv: /\. (\d+) attempts? remaining\./,
};

function opcionesDe(code, detail) {
  const opts = {};
  // Del sobre {error, message, ...}: todo campo simple sirve de variable.
  if (detail && typeof detail === 'object') {
    for (const [k, v] of Object.entries(detail)) {
      if (k !== 'message' && (typeof v === 'string' || typeof v === 'number')) opts[k] = v;
    }
  }
  const texto = typeof detail === 'string' ? detail : (detail && detail.message);
  if (typeof texto === 'string') {
    const extractor = EXTRACTORES[code];
    if (extractor) {
      const m = texto.match(extractor[0]);
      if (!m) return null;  // el texto no es el esperado: mejor no traducir
      extractor[1].forEach((nombre, i) => { opts[nombre] = m[i + 1]; });
    }
    const plural = EXTRACTORES_PLURAL[code];
    if (plural) {
      const m = texto.match(plural);
      if (!m) return null;
      opts.count = parseInt(m[1], 10);
    }
  }
  if (code in CAMPO_DE_PLURAL && CAMPO_DE_PLURAL[code]) {
    const n = opts[CAMPO_DE_PLURAL[code]];
    if (typeof n !== 'number') return null;
    opts.count = n;
  }
  return opts;
}

// Devuelve el texto traducido, o null si este bundle no conoce el código (o si el
// detail no trae las variables que la frase necesita).
function traducePorCodigo(code, detail) {
  if (!code || typeof code !== 'string') return null;
  const clave = `common:errors.${code}`;
  const opts = opcionesDe(code, detail);
  if (opts === null) return null;
  // `i18n.exists` responde por la clave base; para los plurales hay que preguntar por
  // la forma que i18next va a elegir, así que se comprueba con las mismas opciones.
  if (!i18n.exists(clave, opts)) return null;
  const texto = i18n.t(clave, opts);
  // Cinturón: si `t()` devolvió la clave (locale aún no cargado), no se pinta.
  return (typeof texto === 'string' && texto && texto !== clave && texto !== `errors.${code}`)
    ? texto
    : null;
}

function translateBackendError(detail) {
  if (typeof detail !== 'string') return detail;
  const key = BACKEND_ERROR_MAP[detail];
  if (key) return i18n.t(key);
  return detail; // not in map — pass through unchanged (still EN)
}

// El sobre Tier B: `{error|code, message, ...}`. El objeto NO se aplana — las pantallas
// ramifican por `detail.error` / `detail.code` y leen sus números — así que lo único que
// cambia es el `message`, traducido en su sitio.
function traduceSobre(detail, errorCode) {
  const code = detail.error || detail.code;
  // El código hermano manda sobre el del sobre: es más específico. `insufficient_credits`,
  // por ejemplo, lo comparten la subida normal y el finalizar-división, y cada una dice
  // una cosa distinta — traducir por el del sobre pintaría el mensaje equivocado.
  const texto = traducePorCodigo(errorCode, detail) || traducePorCodigo(code, detail);
  if (!texto) return detail;
  return { ...detail, message: texto };
}

// REMATES-1-2-1 (remate): un aviso de ÉXITO también puede traer su código hermano
// (`message_code` junto a `message`, como `error_code` junto a `detail`). Se traduce
// aquí, con la misma pieza y las mismas reglas: un código que este bundle no conozca
// deja el inglés intacto. Sin código, la respuesta pasa tal cual — la MISMA.
function traduceAviso(res) {
  const cuerpo = res && res.data;
  if (cuerpo && typeof cuerpo === 'object' && cuerpo.message_code
      && typeof cuerpo.message === 'string') {
    const texto = traducePorCodigo(cuerpo.message_code, cuerpo.message);
    if (texto) cuerpo.message = texto;
  }
  return res;
}

api.interceptors.response.use(
  (res) => traduceAviso(res),
  async (err) => {
    // Format 429 rate limit messages before they reach component handlers
    if (err.response?.status === 429 && err.response?.data?.detail) {
      err.response.data.detail = formatRateLimitMessage(err.response.data.detail);
    }
    // ── TANDA 8 (#168): la traducción del texto de servidor, en un solo sitio ──
    const cuerpo = err.response?.data;
    const detalle = cuerpo?.detail;
    if (detalle !== undefined && detalle !== null) {
      if (typeof detalle === 'object') {
        // (b) el sobre Tier B: se traduce el `message`, el objeto sigue entero.
        cuerpo.detail = traduceSobre(detalle, cuerpo.error_code);
      } else if (typeof detalle === 'string') {
        // (a) el código hermano; (c) si no lo hay o no se conoce, el mapa de frases de
        // siempre, que a su vez pasa en crudo lo que no está en él.
        cuerpo.detail = traducePorCodigo(cuerpo.error_code, detalle)
          || translateBackendError(detalle);
      }
    }

    const originalRequest = err.config;

    const skipRefreshPaths = ['/auth/refresh', '/auth/login', '/auth/signup', '/auth/guest', '/auth/google-callback', '/auth/apple-callback', '/auth/google-native', '/auth/apple-native', '/auth/logout'];
    if (skipRefreshPaths.some(p => originalRequest.url?.includes(p))) {
      return Promise.reject(err);
    }

    if (err.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => reintentoTrasRefresh(originalRequest, err));
      }

      originalRequest._retry = true;
      const refreshStartedAt = Date.now();
      isRefreshing = true;

      try {
        let res;
        if (IS_APP_BUILD) {
          // Native single-flight refresh: the refresh token lives in the store
          // (no cookie), so it travels in the BODY with the native signal.
          const stored = await getTokens();
          // Pasada final 3: el refresh también viaja por el transporte nativo —
          // es una de las ops que el microscopio vio morir en la ventana
          // post-NET.UP, y va por axios crudo (no por la instancia), así que
          // necesita el adapter explícito.
          res = await axios.post(`${API_BASE}/api/auth/refresh`, withNativeSignal({ refresh_token: stored?.refresh_token }), { withCredentials: false, adapter: nativeHttpAdapter });
          // ROTATION (F3): the refresh token just used is now dead and the
          // response carries the NEW pair. Persist it BEFORE releasing the queue
          // so every retried request re-runs the request interceptor and picks
          // up the FRESH access token — otherwise a queued config would replay
          // with its stale Authorization header.
          await setTokens(res.data);
        } else {
          res = await axios.post(`${API_BASE}/api/auth/refresh`, {}, { withCredentials: true });
        }
        _csrfToken = res.data.csrf_token;
        _lastSessionAt = Date.now();
        isRefreshing = false;
        processQueue(null);
        return reintentoTrasRefresh(originalRequest, err);
      } catch (refreshErr) {
        isRefreshing = false;

        // Pasada final 2 — microscopio: QUÉ forma tuvo el fallo del refresh
        // (solo registro; la clasificación de abajo no cambia ni un bit).
        if (isNetworkShapedError(refreshErr)) logNetError('refresh', refreshErr);

        // A new session was established while this stale refresh was in-flight.
        // Don't expire the session — the new one is valid. Retry queued requests.
        if (_lastSessionAt > refreshStartedAt) {
          processQueue(null);
          return Promise.reject(err);
        }

        // Las requests encoladas fallan con el error del refresh (si fue de red,
        // fallan con el error de red — correcto: sus llamadores ven el fallo).
        processQueue(refreshErr);

        // F6.α — red ≠ token inválido. Solo un 401/403 del refresh prueba que la
        // sesión murió; un timeout/red caída/5xx NO. Antes este catch era
        // incondicional y un parpadeo de red borraba el Keychain (nativo) y
        // expulsaba a login (web). En transitorio: tokens y CSRF intactos, sin
        // _onSessionExpired — el siguiente 401 con red viva reintenta el refresh
        // por el flujo normal.
        if (isAuthRejection(refreshErr)) {
          _csrfToken = '';
          // Native: the refresh token is unrecoverable — drop the store so the next
          // app open starts from a clean logged-out state.
          if (IS_APP_BUILD) await clearTokens();

          // Let React handle the redirect via ProtectedRoute — no hard window.location redirect.
          if (_onSessionExpired) {
            _onSessionExpired();
          }
        }
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

// F6.7b post-smoke — baja del registro de en-vuelo: en cuanto la petición
// termina (bien o mal) deja de ser cancelable.
//
// ORDEN load-bearing: va DESPUÉS del interceptor de 401/refresh de arriba, no
// antes. Dos razones, y la primera basta:
//   1. `api.interceptors.response.handlers[0]` es una referencia fijada por los
//      tests de clasificación del refresh (api.native.test.js,
//      api.refreshClassification.test.js), que invocan ESE handler directo para
//      probar el contrato de "red caída no es 401" sin levantar red. Colarse en
//      el índice 0 les cambiaba el sujeto bajo los pies.
//   2. El camino de reintento tras refrescar (`api(originalRequest)`) resuelve
//      DENTRO de aquel handler; con este de segundo, la baja ocurre igual —
//      por `fulfilled` si el reintento salió bien, por `rejected` si no — y la
//      petición original sigue siendo cancelable mientras el refresh vuela, que
//      es justo lo que se quiere si la señal se cae a media renovación.
//
// F6.8 T5-4 — LA EVIDENCIA, y por qué viaja en ESTE handler y no en el de
// arriba: aquí pasan TODAS las respuestas exitosas, incluidas las de un
// reintento tras refrescar (que resuelve dentro del handler anterior). Una
// respuesta 2xx es la prueba más dura de red que esta app puede tener, así que
// escribe UP en el veredicto — app-only, gateado dentro de la propia función.
// No cambia quién sale ni cuándo: nadie consulta el veredicto para decidir si
// lanza una petición, y el `set()` del veredicto corta los repetidos, así que
// éxito tras éxito no re-emite. Handler de éxito ÚNICAMENTE: un 5xx o un
// timeout no prueban nada y el camino de error queda byte-idéntico.
//
// REMATE-266 — Y AHORA EL CAMINO DE ERROR TAMBIÉN ALIMENTA EL VEREDICTO, hacia
// abajo. Ésta es la mitad que faltaba y la razón entera del incidente del
// 2026-08-16: en todo el repo no existía un solo escritor de DOWN a partir de
// una petición fallida, así que con red zombie (interfaz viva, internet muerto)
// el veredicto no podía bajar por ningún camino.
//
// LA FRONTERA ES `isOfflineShapedError`, NO `isNetworkShapedError`, y la
// diferencia es load-bearing: su hermana mete en la misma bolsa los 5xx y los
// 429, que son fallos con RESPUESTA — o sea, prueba de que había señal viva.
// Alimentar la máquina con ésos convertiría un backend con hipo en un "sin
// conexión" con la red perfecta, que es exactamente el bug C-2 que la frontera
// EH-2 se escribió para cerrar. Sin `response` la petición NUNCA LLEGÓ al
// servidor (red caída, DNS, timeout), y eso —y sólo eso— es evidencia de que no
// hay internet. Bajo lie-fi el caso real es justo ése: peticiones que se cuelgan
// hasta agotar su techo y vuelven sin respuesta.
//
// La otra guarda es `isOwnCancel` (arriba): una cancelación nuestra tiene forma
// de red pero no es un hecho de red.
api.interceptors.response.use(
  (res) => { releaseController(res.config); markNetworkEvidenceUp(); return res; },
  (err) => {
    releaseController(err && err.config);
    if (isOfflineShapedError(err) && !isOwnCancel(err)) markNetworkEvidenceDown();
    return Promise.reject(err);
  },
);

// El disparador de la cancelación. Vive aquí, junto al registro que gobierna:
// api.js es el dueño de sus peticiones en vuelo, y "si el vigía dice que no hay
// red, suéltalas" es una política de este módulo, no de la pantalla que las
// pidió. El vigía es app-only, así que en web esto no se dispara jamás.
//
// REMATE-266 — EL CONTEO SE LEE (línea 4 del recon §4.2). `abortAllInFlight()`
// devolvía cuántas soltaba desde F6.7b y NADIE lo leía; escribirlo mide la
// tormenta del flapping sin agregar un solo cálculo. Sólo cuando hubo víctimas:
// un DOWN con la app quieta no gasta una entrada del anillo.
subscribeNetworkVerdict((verdict) => {
  if (verdict !== NET_VERDICT.DOWN) return;
  const soltadas = abortAllInFlight();
  if (soltadas > 0) {
    try { diagLog('NET.ABORTS', { n: soltadas }); } catch (e) { /* el visor jamás decide una cancelación */ }
  }
});

export default api;

export async function logAnalyticsEvent(eventType, action = 'completed', details = {}) {
  try {
    await api.post('/analytics/event', { event_type: eventType, action, details });
  } catch (e) {
    // Silently fail — never block UX for tracking
  }
}
